/**
 * StatusChip Component
 *
 * Purpose: Reusable status chip for displaying status values across all grids
 * Architecture: Uses centralized metadataService for color and label resolution
 * Dependencies: Material-UI Chip, metadataService
 * Author: Development Team
 * Created: 2025-11-30
 */

import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { metadataService } from '../../services/metadataService';

/**
 * StatusChip Props Interface
 */
export interface StatusChipProps {
  /** The status value (e.g., 'active', 'disabled', 'deleted', 'internal_only') */
  status: string;
  /** Optional size override (defaults to 'small') */
  size?: ChipProps['size'];
  /** Optional variant override (defaults to 'filled') */
  variant?: ChipProps['variant'];
  /** Optional custom label (overrides metadataService.getStatusLabel) */
  label?: string;
  /** Optional aria-label for accessibility */
  'aria-label'?: string;
  /** Optional additional sx props */
  sx?: ChipProps['sx'];
}

/**
 * StatusChip Component
 *
 * Purpose: Standardized status display chip for use across all grids and tables
 * Logic: Uses metadataService.getStatusColor() and getStatusLabel() for consistency
 * Input: status string and optional overrides
 * Output: Styled MUI Chip with appropriate color and label
 *
 * @example
 * // Basic usage
 * <StatusChip status="active" />
 *
 * @example
 * // With custom label
 * <StatusChip status="internal_only" label="Internal" />
 *
 * @example
 * // Outlined variant
 * <StatusChip status="disabled" variant="outlined" />
 */
const StatusChip: React.FC<StatusChipProps> = ({
  status,
  size = 'small',
  variant = 'filled',
  label,
  'aria-label': ariaLabel,
  sx,
}) => {
  const displayLabel = label ?? metadataService.getStatusLabel(status);
  const color = metadataService.getStatusColor(status);

  return (
    <Chip
      label={displayLabel}
      size={size}
      variant={variant}
      color={color}
      aria-label={ariaLabel ?? `Status: ${displayLabel}`}
      sx={sx}
    />
  );
};

export default StatusChip;
